import { createContext, useCallback, useContext, useEffect, useRef, useState, type ReactNode } from "react";
import { PMIcon } from "@/ds/vendor";
import { api } from "@/auth/session";
import { useApp } from "@/app/AppContext";
import "./player.css";

// Podcast quick-player (core-features 4.2 UX): pre-visit prep briefings keep
// playing in a bar along the bottom while the user moves around the app.
// Live: the prep note's generated audio (GET /feature_prep_notes/:id.json →
// audio_url). No audio file (or demo): the browser reads the summary aloud.

export interface Track { id: string; title: string; sub?: string; text: string; locationId?: string | null }
interface PlayerApi { play: (t: Track) => void; current: Track | null; playing: boolean }
const Ctx = createContext<PlayerApi>({ play: () => {}, current: null, playing: false });
export const usePlayer = () => useContext(Ctx);

export function PlayerProvider({ children, onOpenLocation }: { children: ReactNode; onOpenLocation?: (id: string) => void }) {
  const { mode } = useApp();
  const [current, setCurrent] = useState<Track | null>(null);
  const [playing, setPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);
  const [source, setSource] = useState<"audio" | "voice">("voice");
  const [rate, setRate] = useState(1);
  const audio = useRef<HTMLAudioElement | null>(null);
  const tick = useRef<number | undefined>(undefined);
  const startedAt = useRef(0);

  const stopAll = () => {
    audio.current?.pause();
    if (typeof speechSynthesis !== "undefined") speechSynthesis.cancel();
    clearInterval(tick.current);
  };

  const speak = useCallback((t: Track, r: number) => {
    if (typeof speechSynthesis === "undefined") return;
    speechSynthesis.cancel();
    const u = new SpeechSynthesisUtterance(t.text);
    u.rate = r;
    const est = (t.text.split(/\s+/).length / (2.6 * r)); // ~155 wpm
    setDuration(est);
    startedAt.current = Date.now();
    u.onend = () => { setPlaying(false); setProgress(1); clearInterval(tick.current); };
    speechSynthesis.speak(u);
    clearInterval(tick.current);
    tick.current = window.setInterval(() => setProgress(Math.min(0.99, (Date.now() - startedAt.current) / 1000 / est)), 250);
    setPlaying(true);
  }, []);

  const play = useCallback(async (t: Track) => {
    stopAll();
    setCurrent(t); setProgress(0);
    if (mode === "live") {
      try {
        const r = await api<{ audio_url?: string }>(`/feature_prep_notes/${t.id}.json`);
        if (r.audio_url) {
          const a = audio.current ?? new Audio();
          audio.current = a;
          a.src = r.audio_url; a.playbackRate = rate;
          a.ontimeupdate = () => { setProgress(a.duration ? a.currentTime / a.duration : 0); setDuration(a.duration || 0); };
          a.onended = () => setPlaying(false);
          await a.play();
          setSource("audio"); setPlaying(true);
          return;
        }
      } catch { /* fall back to reading the summary */ }
    }
    setSource("voice");
    speak(t, rate);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, rate, speak]);

  const toggle = () => {
    if (!current) return;
    if (source === "audio" && audio.current) {
      if (playing) audio.current.pause(); else void audio.current.play();
      setPlaying(!playing);
    } else if (typeof speechSynthesis !== "undefined") {
      if (playing) { speechSynthesis.pause(); clearInterval(tick.current); setPlaying(false); }
      else if (speechSynthesis.paused) { speechSynthesis.resume(); setPlaying(true); }
      else speak(current, rate);
    }
  };
  const close = () => { stopAll(); setCurrent(null); setPlaying(false); };
  const cycleRate = () => {
    const next = rate === 1 ? 1.25 : rate === 1.25 ? 1.5 : 1;
    setRate(next);
    if (source === "audio" && audio.current) audio.current.playbackRate = next;
    else if (current && playing) speak(current, next);
  };
  useEffect(() => () => stopAll(), []);

  const fmt = (s: number) => `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, "0")}`;

  return (
    <Ctx.Provider value={{ play, current, playing }}>
      {children}
      {current && (
        <div className="player" role="region" aria-label="Prep briefing player">
          <button className="player-play" onClick={toggle} aria-label={playing ? "Pause" : "Play"}>
            {playing ? <span className="player-pause" aria-hidden="true"><i /><i /></span> : <PMIcon name="play-filled" size={22} fg="currentColor" accent="currentColor" />}
          </button>
          <div className="player-main">
            <div className="player-title">
              <span className="player-eyebrow">{source === "audio" ? "PREP PODCAST" : "PREP BRIEFING · READ ALOUD"}</span>
              <button className="player-name" onClick={() => current.locationId && onOpenLocation?.(current.locationId)}>{current.title}</button>
              {current.sub && <span className="player-sub">{current.sub}</span>}
            </div>
            <div className="player-track" aria-hidden="true">
              <div className={`player-wave ${playing ? "is-playing" : ""}`}>
                {Array.from({ length: 64 }, (_, i) => <i key={i} style={{ height: `${22 + ((i * 37) % 60)}%` }} className={i / 64 <= progress ? "on" : ""} />)}
              </div>
              <span className="player-time">{fmt(progress * duration)} / {fmt(duration)}</span>
            </div>
          </div>
          <button className="player-rate" onClick={cycleRate} aria-label="Playback speed">{rate}×</button>
          <button className="player-close" onClick={close} aria-label="Close player">✕</button>
        </div>
      )}
    </Ctx.Provider>
  );
}
